import { AddressElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import strings from 'mobile_web/strings'
import { CreateOrderBody, InAppPurchaseWebPrice, Order, OrderItem } from 'mytours-read-api-typescript'
import { getDirectApi } from 'mytours-read-common'
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react'
import { formatCurrency } from '../functions'
import { Button, Card, CardContent, CardHeader, Spinner, Input, Text } from '@mytours/components'
import Modal from '../../common/components/modal'

interface Props {
	titleNode: JSX.Element
	webPrice?: InAppPurchaseWebPrice
	itemId: number
	itemType: OrderItem.ItemTypeEnum
	onClose: () => void
}

const PayModal: FC<Props> = ({ titleNode, webPrice: { amount, cents, currency }, itemId, itemType, onClose }) => {
	const stripe = useStripe()
	const elements = useElements()

	const [address, setAddress] = useState({
		line1: '',
		line2: '',
		city: '',
		state: '',
		postal_code: '',
		country: '',
	})
	const [email, setEmail] = useState('')
	const [message, setMessage] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [taxAmount, setTaxAmount] = useState<number | null>(null)
	const [taxLoading, setTaxLoading] = useState(false)

	const initialized = useRef(false)
	const taxDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		if (taxDebounceRef.current) {
			clearTimeout(taxDebounceRef.current)
		}

		setTaxLoading(true)

		taxDebounceRef.current = setTimeout(() => {
			void (async () => {
				try {
					const result = await getDirectApi().orderCalculateTax({
						items: [
							{
								item_type: itemType,
								item_id: itemId,
							},
						],
						address,
					})
					setTaxAmount(result.tax_amount)
				} catch {
					setTaxAmount(null)
				} finally {
					setTaxLoading(false)
				}
			})()
		}, 2000)

		return () => {
			if (taxDebounceRef.current) {
				clearTimeout(taxDebounceRef.current)
			}
		}
	}, [address, itemId, itemType])

	if (!stripe || !elements) {
		return null
	}

	if (!initialized.current) {
		const styles = window.getComputedStyle(document.body)
		// We need to set the correct amount before we can collect payment method
		elements.update({
			mode: 'payment',
			amount: cents,
			currency: currency.toLowerCase(),
			appearance: {
				variables: {
					fontFamily: styles.getPropertyValue('--ion-font-family'),
					colorText: styles.getPropertyValue('--pay-modal-text'),
					colorBackground: styles.getPropertyValue('--pay-modal-background'),
					colorPrimary: styles.getPropertyValue('--pay-modal-accent'),
				},
			},
		})
		initialized.current = true
	}

	const price = formatCurrency(currency, amount + (taxAmount ?? 0))

	const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
		setEmail(e.target.value)
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		setIsLoading(true)

		// Trigger form validation and wallet collection
		const submit = await elements.submit()
		if (submit.error) {
			setMessage(submit.error.message)
			setIsLoading(false)
			return
		}

		let order: Order
		try {
			order = await getDirectApi().orderCreate({
				order_type: CreateOrderBody.OrderTypeEnum.Stripe,
				email,
				items: [
					{
						item_type: itemType,
						item_id: itemId,
					},
				],
				address,
			})
		} catch (error) {
			setMessage('An unexpected error occurred.')
			setIsLoading(false)
			return
		}
		const clientSecret = order.stripe_payment_intent_client_secret

		if (order.amount !== amount || order.currency !== currency) {
			setMessage(
				'No payment has been collected yet. The price has been updated to ' +
					formatCurrency(order.currency, Number(order.amount)) +
					'. please refresh the page to try again.',
			)
			return
		}

		const redirectUrl = new URL(window.location.href)
		redirectUrl.searchParams.set('order-id', String(order.id))
		const { error } = await stripe.confirmPayment({
			elements,
			clientSecret,
			confirmParams: {
				return_url: redirectUrl.toString(),
				receipt_email: email,
			},
		})

		// A successful payment will redirect to the order page and never reach this point
		// Stripe suggests to show the error message of these 2 specific types to the user
		if (error.type === 'card_error' || error.type === 'validation_error') {
			setMessage(error.message)
		} else {
			setMessage('An unexpected error occurred.')
		}

		setIsLoading(false)
	}

	return (
		<Modal isOpen={true} backdropDismiss={false} cssClass="pay-modal">
			<Card variant="ios">
				<CardHeader>
					<Button variant="clear" className="close" onClick={onClose} aria-label={strings.close}>
						<i className="fal fa-xmark" />
					</Button>
				</CardHeader>
				<CardContent className="pay-modal-content">
					{titleNode}
					{taxAmount > 0 ? (
						<table className="price-breakdown">
							<tbody>
								<tr>
									<td>{strings.subtotal}</td>
									<td>{formatCurrency(currency, amount)}</td>
								</tr>
								<tr>
									<td>{strings.tax}</td>
									<td>{formatCurrency(currency, taxAmount)}</td>
								</tr>
								<tr className="total">
									<td>{strings.total}</td>
									<td>{price}</td>
								</tr>
							</tbody>
						</table>
					) : (
						<h3>{formatCurrency(currency, amount)}</h3>
					)}
					{taxLoading && (
						<Spinner
							variant="crescent"
							aria-label={strings.loading}
							color="var(--body-color)"
							style={{ marginBottom: '1rem' }}
						/>
					)}
					<form onSubmit={handleSubmit}>
						<label htmlFor="email">{strings.email_label}</label>
						{/* The id sits on the native field, so the label above now names it too. */}
						<Input
							variant="ios"
							value={email}
							onChange={handleChange}
							id="email"
							aria-label={strings.email_label}
							type="email"
							required
							className="email-input"
						/>

						<AddressElement
							options={{ mode: 'billing', fields: { phone: 'never' } }}
							onChange={event => setAddress(event.value.address)}
						/>

						<PaymentElement onLoadError={({ error }) => setMessage(error.message)} />

						{message && (
							<Text variant="inherit" className="error-message">
								{message}
							</Text>
						)}

						<Button
							variant="primary"
							aria-label={strings.pay_now}
							className="-submit"
							type="submit"
							disabled={isLoading || !stripe || !elements}
						>
							{isLoading ? (
								<Spinner variant="crescent" aria-label={strings.loading} />
							) : taxAmount !== null ? (
								strings.formatString(strings.pay_x_amount, { amount: price })
							) : (
								strings.pay_now
							)}
						</Button>
					</form>
				</CardContent>
			</Card>
		</Modal>
	)
}

export default PayModal
